import * as React from 'react'

import { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import type { ICreationFieldOptions } from '~/client/src/shared/components/ResourceViewEdit/useResourceFields'
import { IWarningNote } from '~/client/src/shared/components/WarningNote/WarningNote'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { IQuickFilters } from '~/client/src/shared/hooks/useResourceQuickFilters'
import type { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { IResourceGroupPresentation } from '~/client/src/shared/resources/resourceLink'

import type { IPickerGrouping } from './buildPickerGroupConfig'
import { IResourcePickerState } from './useResourcePickerState'

export type ResourceFormData = Record<string, unknown>

interface IStringItem {
  id: string
  label: string
}

interface IResourcePickerContext extends Omit<
  IResourcePickerState,
  'setSortKey' | 'setSortOrder'
> {
  // Sets key and direction together and notifies the field defaults.
  setSort: (key: string | null, order?: SortOrder) => void
  /** Where clearing the grouping, or a grouping no longer offered, lands. */
  fallbackGroupByKey: string | null
  projectId?: string
  /** The primary type; per record, read getRecordTypeId. */
  resourceTypeId: string
  loading: boolean
  sortOptions: IOptionItem[]
  groupByOptions: IOptionItem[]
  filterCategories: IDrillDownFilterCategory<IStringItem>[]
  onSelect: (ids: string[]) => void
  onCreateResource?: (formData: ResourceFormData) => Promise<string | null>
  allowSelectExisting?: boolean
  onClose?: () => void
  quickFilters?: IQuickFilters
  templateAllowList: string[] | null
  resourceSchema?: ResourceSchema
  getGroupPresentation: (
    groupKey: string,
    count?: number,
  ) => IResourceGroupPresentation | null
  /** The active grouping; null when the list is flat or a tree. */
  grouping: IPickerGrouping | null
  /** The host's records, before its predicate and the field's filters. */
  unfilteredRecords: IResourceRecord[]
  /** What can be picked: past the host's predicate and the field's filters, before the
   * search and the picker's own filters, which only narrow what is shown. */
  pickableIds: ReadonlySet<string>
  /** Predicate to grey out non-selectable rows (e.g. company-restricted). */
  isItemDisabled?: (recordId: string) => boolean
  /** Values a new record's form starts from. Keep stable. */
  creationRootDefaults?: Record<string, any>
  /** Keep stable. */
  creationFieldOptions?: ICreationFieldOptions
  /** A note rendered beneath the row. */
  getNote?: (recordId: string) => IWarningNote | null
  /** A row press: toggles, and commits at once when `commitsOnPick`. */
  pickItem: (id: string) => void
  /** Single mode without staging: a pick commits, so there is no Apply. */
  commitsOnPick: boolean
  /** Apply may commit an empty selection. */
  allowEmptySelection?: boolean
  /** The resource type a record belongs to; constant for a single-type picker. */
  getRecordTypeId: (record: IResourceRecord) => string
  getTypeIcon: (resourceTypeId: string) => React.ReactNode
  /** The renderItem of a record's own type, if it has one; undefined when no type does. */
  getSourceRenderItem?: (
    record: IResourceRecord,
  ) => ((record: IResourceRecord) => React.ReactNode) | undefined
}

export const ResourcePickerContext =
  React.createContext<IResourcePickerContext | null>(null)

export function useResourcePickerContext(): IResourcePickerContext {
  const ctx = React.useContext(ResourcePickerContext)
  if (!ctx) {
    throw new Error('ResourcePicker.* must be used within <ResourcePicker>')
  }
  return ctx
}
